/**
 * OwnerInboundFunnelPanel — Owner-only tile that surfaces the
 * catalogue-watcher → provisioned-tenant funnel for the last N days.
 *
 * Closes the loop visually so the Owner can see inbound demand convert
 * into live tenants without reading the leads table. Each row in the
 * recent-provisions list links to the audit-trail entry that documents
 * the action.
 *
 * Source: GET /api/owner/leads/inbound-funnel
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Activity, ArrowRight, CheckCircle2, Mail, Rocket } from "lucide-react";

const WINDOW_OPTIONS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

function pctFmt(rate) {
  if (!rate && rate !== 0) return "—";
  return `${(rate * 100).toFixed(0)}%`;
}

function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const ts = new Date(iso);
    const mins = Math.floor((Date.now() - ts.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 48) return `${hours}h ago`;
    return ts.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

export function OwnerInboundFunnelPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/owner/leads/inbound-funnel?days=${days}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled)
          setErr(
            formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="owner-inbound-funnel-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-ek-purple/10">
            <Activity className="h-4 w-4 text-ek-purple" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Inbound funnel — catalogue → tenant
            </h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Where catalogue-watcher leads turn into live workspaces. Every
              provision is audit-logged.
            </p>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-black/10 bg-white p-0.5" role="tablist">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              role="tab"
              aria-selected={days === opt.value}
              data-testid={`owner-funnel-window-${opt.value}`}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                days === opt.value
                  ? "bg-ek-purple text-white"
                  : "text-ek-ink/70 hover:bg-black/5"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <p className="mt-4 text-sm text-ek-ink/60" data-testid="owner-funnel-loading">
          Loading…
        </p>
      )}
      {err && !loading && (
        <p className="mt-4 text-sm text-red-600" data-testid="owner-funnel-error">
          {err}
        </p>
      )}

      {!loading && !err && data && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Watcher leads"
              value={data.watcher_leads}
              testid="owner-funnel-watchers"
            />
            <Stat
              label="Tenants provisioned"
              value={data.tenants_provisioned}
              tone="green"
              testid="owner-funnel-provisioned"
            />
            <Stat
              label="Welcome emails sent"
              value={data.welcomes_sent}
              tone={data.welcomes_sent > 0 ? "green" : "neutral"}
              testid="owner-funnel-welcomes"
            />
            <Stat
              label="Conversion rate"
              value={pctFmt(data.conversion_rate)}
              tone={data.conversion_rate >= 0.2 ? "green" : "amber"}
              testid="owner-funnel-conversion"
            />
          </div>

          {data.recent.length > 0 ? (
            <ul
              className="mt-4 divide-y divide-black/5 rounded-xl border border-black/5 bg-black/[0.015]"
              data-testid="owner-funnel-recent"
            >
              {data.recent.map((r) => (
                <li
                  key={`${r.tenant_id}-${r.lead_id}`}
                  className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
                  data-testid={`owner-funnel-row-${r.tenant_id}`}
                >
                  <Rocket className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                  <span className="font-mono text-xs text-ek-ink">
                    {r.tenant_id}
                  </span>
                  {r.requested_connector && (
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800">
                      {r.requested_connector}
                    </span>
                  )}
                  <ArrowRight className="h-3 w-3 text-ek-ink/40" />
                  <span className="text-xs text-ek-ink/60">
                    {fmtAge(r.at)} · {r.actor_email || "—"}
                  </span>
                  {r.welcome_email_sent && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
                      title="Welcome email sent via Resend"
                    >
                      <Mail className="h-3 w-3" /> welcomed
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p
              className="mt-4 rounded-lg border border-dashed border-black/10 bg-white p-4 text-xs text-ek-ink/55"
              data-testid="owner-funnel-empty"
            >
              No tenants provisioned from leads in the last {data.window_days} days.
              When you click <b>Provision tenant</b> on a catalogue-watcher lead,
              it shows up here with its audit trail.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneClass = {
    green: "text-emerald-700",
    amber: "text-amber-700",
    neutral: "text-ek-ink",
  }[tone || "neutral"];
  return (
    <div className="rounded-xl border border-black/5 bg-black/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-ek-ink/55">
        {label}
      </div>
      <div
        className={`mt-0.5 inline-flex items-center gap-1.5 text-base font-semibold ${toneClass}`}
        data-testid={testid}
      >
        {tone === "green" && <CheckCircle2 className="h-3.5 w-3.5" />}
        {value}
      </div>
    </div>
  );
}

export default OwnerInboundFunnelPanel;
